/**
 * Tracks the lock the backend puts on an OTP device after failed verification
 * attempts (HTTP 429 from the verify/confirm endpoints).
 *
 * Usage:
 *
 * mixins: [otpThrottle],
 *
 * const { error } = await this.http({ ..., dontShowError: true });
 * if (this.handleOtpThrottle(error)) { ... }  // otpLocked / otpLockRemaining
 */

export default {
  data() {
    return {
      otpLockedUntil: null,
      otpNow: Date.now(),
      otpTickInterval: null,
    };
  },

  computed: {
    otpLocked() {
      return !!(this.otpLockedUntil && this.otpLockedUntil > this.otpNow);
    },
    otpLockRemaining() {
      // format the remaining time as HH:MM:SS
      // this way it has a consistent width so changes every second
      // do not cause layout shifts and look natural
      if (!this.otpLockedUntil) return null;
      const totalSeconds = Math.ceil(
        Math.max(0, this.otpLockedUntil - this.otpNow) / 1000,
      );
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      return [hours, minutes, seconds]
        .map((n) => String(n).padStart(2, "0"))
        .join(":");
    },
  },

  methods: {
    /** Record the lock if `error` is a throttling response; returns whether it was. */
    handleOtpThrottle(error) {
      if (error?.response?.status !== 429) return false;
      // the backend sends a relative delay so that client clock skew does not matter
      const retryAfter = Number(error.response.data?.retry_after) || 1;
      this.otpNow = Date.now();
      this.otpLockedUntil = this.otpNow + retryAfter * 1000;
      return true;
    },
  },

  mounted() {
    this.otpTickInterval = setInterval(() => {
      this.otpNow = Date.now();
    }, 1000);
  },

  beforeUnmount() {
    clearInterval(this.otpTickInterval);
  },
};
